/**
 * useEnsureWorkspaceForAuthedUser — Patch 37.4.13 (Google Auth Post-Login Hang Fix).
 *
 * Problem this fixes
 * ──────────────────
 * Google OAuth (``/api/v1/auth/oauth/emergent/exchange``) creates the
 * user record but never provisions a workspace. The visitor lands on
 * ``/home`` authenticated but with ``workspaces = []`` and
 * ``currentWorkspaceId = null``. Every workspace-scoped page then
 * renders an indefinite "Loading workspace…" placeholder (Business
 * Brain, Brand Brain, AI Studio, Workspace hub, …) because
 * ``useCurrentWorkspace()`` returns ``null`` and the pages' guard
 * clauses treat that as still-loading.
 *
 * From the user's POV: they log in with Google, click a menu item,
 * and the page hangs for several minutes.
 *
 * Contract
 * ────────
 * When the bootstrap has finished (``hydrated=true``), the visitor has
 * a real access token, no guest session is active, and the workspaces
 * list is empty, redirect the visitor to ``/genesis`` even if an older
 * entry point sent them to ``/workspaces/new``. Genesis owns first-workspace
 * creation; customers with an existing workspace can still use the
 * separate manual path to add another workspace.
 *
 * We must NOT redirect while the onboarding auto-create bridge is
 * about to fire (an ``mos.onboarding.draft.v1`` in localStorage will
 * become a workspace in the next tick via
 * ``useAutoCreateWorkspaceFromOnboarding``). Racing that hook would
 * yank the visitor away from a bridge that was about to succeed.
 *
 * The redirect fires once per mount thanks to ``firedRef``.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/entities/auth/store";
import { useWorkspaceStore } from "@/app/store/workspace-store";

/** Genesis and authenticated onboarding steps must not be interrupted.
 *  The manual workspace creator is deliberately absent: zero-workspace
 *  customers entering there still need the first-workspace Genesis flow. */
const NO_REDIRECT_PATHS = new Set<string>([
  "/onboarding",
  "/genesis",
  "/welcome",
  "/login",
  "/auth/callback",
]);

/** True when a draft workspace will be auto-created shortly by
 *  ``useAutoCreateWorkspaceFromOnboarding``. Avoids fighting that
 *  bridge — if it will succeed, the workspaces list will populate
 *  and this hook becomes a no-op. */
function onboardingBridgeWillFire(): boolean {
  try {
    const raw = window.localStorage.getItem("mos.onboarding.draft.v1");
    if (!raw) return false;
    const draft = JSON.parse(raw) as {
      businessName?: string;
      description?: string;
      goals?: string[];
    };
    return Boolean(
      (draft.businessName && draft.businessName.trim())
      || draft.description
      || (draft.goals && draft.goals.length > 0),
    );
  } catch {
    return false;
  }
}

export function useEnsureWorkspaceForAuthedUser(): void {
  const token = useAuthStore((s) => s.accessToken);
  const guestActive = useAuthStore((s) => s.guest.active);
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const workspacesLength = useWorkspaceStore((s) => s.workspaces.length);
  const navigate = useNavigate();
  const location = useLocation();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!token) return;
    if (guestActive) return;
    if (!hydrated) return;
    if (workspacesLength !== 0) return;
    if (NO_REDIRECT_PATHS.has(location.pathname)) return;
    if (onboardingBridgeWillFire()) return;

    firedRef.current = true;
    navigate("/genesis", {
      replace: true,
      state: { from: location.pathname },
    });
  }, [token, guestActive, hydrated, workspacesLength, location.pathname, navigate]);
}
